import React, { FC, Suspense, useContext, useEffect, useReducer, useState } from 'react';
import SecureContent, { AuthenticationResult } from 'src/core/services/authentication';
import { AppConfig } from 'src/inversify.config';
import Shell, { setHeader, setUsername } from 'src/core/ui/shell';
import { Button, Col, Menu, Row, Spin, Tooltip } from 'antd';
import {
  UserOutlined,
  AuditOutlined,
  TeamOutlined,
  BankOutlined,
  LogoutOutlined,
  LoadingOutlined,
  KeyOutlined,
  ReadOutlined,
  FundOutlined,
  SunFilled,
  MoonFilled,
  LaptopOutlined,
} from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import PartnerInfo from '../common/partner-info';
import OrganizationBox from './organization-box';
import { container } from 'src/inversify.config';
import PermissionService from 'src/common/permission-service';
import { useCurrentUserPermissionCheckInMainOrg, useCurrentUserTenantPermissionsFilter } from 'src/common/permission-hooks';
import { IdentityType, PermissionsConstants } from 'src/common/types';
import i18next from 'i18next';
import logo from 'src/core/assets/images/SPT_Logo Full_2end_Digital_White-Red.png';
import smallLogo from 'src/core/assets/images/Small-logo-Red2.png';
import useMediaQuery from 'src/common/hooks/use-media-query';
import {
  OrganizationExportImportContext,
  organizationExportImportReducer,
  organizationExportImportContextInitialData,
} from './organizations/organization-export-context';
import { LicenseCreationAttributesStore } from 'src/stores/licensing-store';
import { Link, Routes, Route, useNavigate, useLocation } from 'react-router-dom';
import { AppConfiguration } from 'src/core/services/configuration.service';
import { ThemeContext } from 'src';
import { UserItemStore, UserProfileItemStore } from 'src/stores/users-store';
import { useThemeDetector } from 'src/common/hooks/use-theme-detector';
import { ItemType } from 'antd/es/menu/interface';
import { Theme, ThemeSelection } from 'src/common/themes';
import LanguageSelect from 'src/common/components/business/language-select';
import HomePage from './home';
import OrganizationsPage from './organizations';
import UsersPage from './users';
import RolesPage from './roles';
import PartnerRolesPage from './partner-roles';
import GroupsPage from './groups';
import LicensesPage from './licenses';
import EulaPage from './eula';
import StatisticsPage from './statistics';
import UnauthorizedPage from './unauthorized';
import NotFoundPage from './not-found';
import { EventType, ScheduledEventItemStore, ScheduledEventSummaryStore } from 'src/stores/scheduled-events-store';
import { Languages } from 'src/i18n';
import moment from 'moment';
import { Query } from 'src/core/stores/data-store';
import { getDateFormat } from 'src/common/utils';

import { Notifications } from 'src/pages/notifications/notifications';
import { useSessionNotifications } from 'src/pages/notifications/notifications-session-store';
import { NotificationsReceiver } from 'src/pages/notifications/notifications-receiver';
import NotificationsTableView from './notifications/notifications-table';
import { create } from 'zustand';
import { NotificationsContext } from './notifications/notifications-context';

const permissions = [
  PermissionsConstants.CreateOrganization,
  PermissionsConstants.DeleteOrganization,
  PermissionsConstants.ManageOrganizationInfo,
  PermissionsConstants.BlockOrganization,

  PermissionsConstants.ManageUsers,
  PermissionsConstants.ManageRoles,
  PermissionsConstants.ManageGroups,
  PermissionsConstants.ManageLicenses,
  PermissionsConstants.ApproveLicenses,
  PermissionsConstants.ManagePartnerOrganization,
];

export const useSideMenu = create<{ selectedSideMenu: string; setSelectedSideMenu: (selected: string) => void }>((set) => ({
  selectedSideMenu: '',
  setSelectedSideMenu: (selected) => set({ selectedSideMenu: selected }),
}));

const AdminIndex: FC = () => {
  const config: AppConfiguration = useContext(AppConfig);
  const permissionSvc = container.get(PermissionService);
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const navigate = useNavigate();

  const selectedKey = pathname.split('/')[1];
  const { selectedSideMenu: selectedMenu, setSelectedSideMenu: setSelectedMenu } = useSideMenu();
  const [authResult, setAuthResult] = useState<AuthenticationResult>();
  const partnerInfo = container.get<PartnerInfo>(PartnerInfo);
  const [unauthorized, setUnauthorized] = useState<boolean>(false);
  const [userPermissionsSet, checkingPermissions] = useCurrentUserTenantPermissionsFilter(authResult, permissions);
  const [hasGlobalLicenseManagerPermission, checkingGlobalLicenseManagerPermission] = useCurrentUserPermissionCheckInMainOrg(
    authResult,
    PermissionsConstants.ManageGlobalLicenses
  );
  const matches = useMediaQuery('(max-width: 768px)');
  const [collapsed, setCollapsed] = useState(matches);
  const [organizationExportState, organizationExportDispatch] = useReducer(
    organizationExportImportReducer,
    organizationExportImportContextInitialData
  );

  const licenseAttributesStore = container.get(LicenseCreationAttributesStore);
  const [licenseManagerRoleValid, setLicenseManagerRoleValid] = useState(false);
  const { themeData, setThemeData } = useContext(ThemeContext);
  const userStore = container.get(UserItemStore);
  const systemUsesDarkTheme = useThemeDetector();

  const [headerMenuOpen, setHeaderMenuOpen] = useState(false);
  const userProfileStore = container.get(UserProfileItemStore);
  const userProfileState = userProfileStore.state;

  const scheduledEventsSummaryStore = container.get(ScheduledEventSummaryStore);
  const scheduledEventsSummaryState = scheduledEventsSummaryStore.state;
  const scheduledEventsItemStore = container.get(ScheduledEventItemStore);
  const scheduledEventsItemState = scheduledEventsItemStore.state;
  const [banner, setBanner] = useState<{ img?: string; link?: string; alt?: string }>();
  const [alert, setAlert] = useState<string>();
  const [notificationsVisible, setNotificationsVisible] = useState(false);
  const [notificationsRevision, setNotificationsRevision] = useState(0);
  const sessionNotifications = useSessionNotifications();

  const updateEventBanners = async (language: Languages) => {
    const now = moment(new Date()).toISOString();
    const activeEventsQuery: Partial<Query> = {
      filter: [`announcementStartDate le ${now} and announcementEndDate ge ${now}`],
      orderBy: [{ field: 'announcementStartDate', direction: 'Ascending', useProfile: false }],
    };
    await scheduledEventsSummaryStore.load(activeEventsQuery);
    const scheduledUpdate = scheduledEventsSummaryState.items.value.find((v) => v.type === EventType.ScheduledUpdate);
    const announcement = scheduledEventsSummaryState.items.value.find((v) => v.type === EventType.Announcement);
    if (scheduledUpdate) {
      const startDate = moment(scheduledUpdate.scheduledUpdateStart).locale(language).format(getDateFormat());
      const startMoment = moment(scheduledUpdate.scheduledUpdateStart).locale(language).format('hh:mm a');
      const hours = scheduledUpdate.announcementDuration;
      const alertText = t('ScheduledMaintenanceText').replace('{0}', startDate).replace('{1}', `${hours}`).replace('{2}', startMoment);
      setAlert(alertText);
    }
    if (announcement) {
      await scheduledEventsItemStore.loadWithSelectedLanguages(announcement.id, language);
      if (scheduledEventsItemState.item.value.announcementInfo.length > 0) {
        const announcementData = scheduledEventsItemState.item.value;
        const localizedData = announcementData.announcementInfo[0];
        setBanner({
          alt: localizedData.text,
          img: localizedData.image,
          link: announcementData.announcementLink,
        });
      }
    }
  };

  const updateTheme = (userTheme: ThemeSelection, updateUserData = true) => {
    switch (userTheme) {
      case ThemeSelection.Auto: {
        const theme = systemUsesDarkTheme ? Theme.Dark : Theme.Light;
        setThemeData(() => ({
          detectSystem: true,
          selectedTheme: theme,
        }));
        break;
      }
      case ThemeSelection.Light: {
        setThemeData(() => ({
          detectSystem: false,
          selectedTheme: Theme.Light,
        }));
        break;
      }
      case ThemeSelection.Dark: {
        setThemeData(() => ({
          detectSystem: false,
          selectedTheme: Theme.Dark,
        }));
      }
    }

    if (updateUserData) {
      userStore.updatePreferredTheme(authResult?.profile['sub'], userTheme);
    }
  };

  const headerMenuEntries: ItemType[] = [];

  if (!checkingPermissions && authResult) {
    headerMenuEntries.push({
      key: 'language-dropdown',
      label: `${t('Language')}:`,
      type: 'group',
      children: [
        {
          key: 'language',
          type: 'item',
          label: (
            <div
              className="dropdown-option-menu"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
              }}
            >
              <LanguageSelect
                stopEventPropagation
                allowClear={false}
                value={userProfileState?.item?.value?.defaultLanguage ?? authResult?.profile['default_lang']}
                onChange={async (v) => {
                  const postData = {
                    defaultLanguage: v,
                    displayName: authResult?.profile['display_name'],
                    email: authResult.email!,
                  };
                  setHeaderMenuOpen(false);
                  let response = await userProfileStore.save(authResult?.profile['sub'], postData as any);
                  if (response?.isSuccess) {
                    i18next.changeLanguage(v);
                    updateEventBanners(v as Languages);
                    document.cookie = `language=${v}`;
                    userProfileState.set((s) => {
                      s = {
                        ...s,
                        item: {
                          ...s?.item,
                          defaultLanguage: v,
                          displayName: authResult?.profile['display_name'],
                          email: authResult.email!,
                          id: authResult.profile['sub'],
                        },
                      };
                      return s;
                    });
                  }
                }}
              />
            </div>
          ),
        },
      ],
    });

    headerMenuEntries.push({
      key: 'theme-menu-dropdown',
      label: `${t('Appearance')}:`,
      type: 'group',
      children: [
        {
          key: 'theme-selection',
          type: 'item',
          label: (
            <Row style={{ width: '80%', margin: 'auto' }}>
              <Col span={8}>
                <Tooltip title={t('LightTheme')}>
                  <Button
                    className={`solid-button ${themeData.selectedTheme === Theme.Light && !themeData.detectSystem ? 'theme-button-active' : ''}`}
                    type="dashed"
                    onClick={() => updateTheme(ThemeSelection.Light)}
                  >
                    <SunFilled />
                  </Button>
                </Tooltip>
              </Col>
              <Col span={8}>
                <Tooltip title={t('DarkTheme')}>
                  <Button
                    className={`solid-button ${themeData.selectedTheme === Theme.Dark && !themeData.detectSystem ? 'theme-button-active' : ''}`}
                    type="dashed"
                    onClick={() => updateTheme(ThemeSelection.Dark)}
                  >
                    <MoonFilled />
                  </Button>
                </Tooltip>
              </Col>
              <Col span={8}>
                <Tooltip title={t('UseSystemTheme')}>
                  <Button
                    className={`solid-button ${themeData.detectSystem ? 'theme-button-active' : ''}`}
                    type="dashed"
                    onClick={() => updateTheme(ThemeSelection.Auto)}
                  >
                    <LaptopOutlined />
                  </Button>
                </Tooltip>
              </Col>
            </Row>
          ),
        },
      ],
    });

    headerMenuEntries.push({
      type: 'divider',
    });

    headerMenuEntries.push({
      label: t('Logout'),
      key: 'logout',
      icon: <LogoutOutlined />,
      onClick: () => authResult?.logout(),
    });
  }

  const getName = () => {
    return `${authResult?.profile['display_name'] ?? ''}`.trimStart().trimEnd();
  };

  useEffect(() => {
    setHeader(t('WelcomBackParam').replace('{0}', `${getName()}`));
    setUsername(getName());

    if (authResult) {
      setSelectedMenu(selectedKey || '/');
      //@ts-ignore
      window.chtlConfig = {
        chatbotId: '4844588994',
        variables: {
          contact_name: getName(),
        },
      };
      const script = document.createElement('script');

      script.src = 'https://chatling.ai/js/embed.js';
      script.id = 'chtl-script';
      script.type = 'text/javascript';

      //@ts-ignore
      script['data-id'] = '4844588994';
      script.async = true;

      document.body.appendChild(script);

      return () => {
        document.body.removeChild(script);
      };
    }
  }, [authResult]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <SecureContent
      config={{
        authority: config!.identityServerUrl,
        redirectUri: `${config!.hostUrl}${process.env.PUBLIC_URL}`,
        silentRedirectUri: `${config!.hostUrl}${process.env.PUBLIC_URL}/silentrefresh.html`,
        resource: config!.scopes,
        clientId: config!.clientId,
        passthrough: config!.passthrough,
      }}
      onAuthentication={async (result) => {
        setAuthResult(result);
        updateEventBanners(result?.profile['default_lang']);
        updateTheme(result.profile['theme'], false);
        if (result && result.error == null && result.profile['tenant_is_partner'] === 'true') {
          setUnauthorized(false);
          i18next.changeLanguage(result?.profile['default_lang']);
          window.history.replaceState(window.history.state, '', window.location.pathname + window.location.search);
          permissionSvc.setCurrentUser(result.profile['sub'], result.profile['tenant_id']);
          partnerInfo.organizationId = result.profile['tenant_id'] as string;
          const roleValid = await licenseAttributesStore.getByUserScope({
            identityType: IdentityType.User,
            identityId: result.profile['sub'],
            scopeId: result.profile['tenant_id'],
          });
          setLicenseManagerRoleValid(roleValid);
        } else {
          setUnauthorized(true);
          permissionSvc.clearCurrentUser();
          navigate(`unauthorized`);
        }
      }}
    >
      {checkingPermissions || checkingGlobalLicenseManagerPermission ? (
        <Spin className="global-spinner" indicator={<LoadingOutlined style={{ fontSize: 50 }} spin />} size="large"></Spin>
      ) : (
        <div className={`shell-wrapper ${collapsed ? '' : 'hide-layout'}`}>
          <OrganizationExportImportContext.Provider value={{ organizationExportState, organizationExportDispatch }}>
            <NotificationsReceiver />
            <Shell
              notificationsVisible={notificationsVisible}
              onNotificationsVisibilityChange={(visible) => {
                setNotificationsVisible(() => visible);
                setNotificationsRevision((prev) => prev + 1);
              }}
              notificationsCount={sessionNotifications.state.unreadNotifications.count}
              notificationsComponent={<Notifications updateVisibility={setNotificationsVisible} revision={notificationsRevision} />}
              warning={alert}
              banner={banner?.img}
              bannerLink={banner?.link}
              bannerAlt={banner?.alt}
              headerMenu={{
                className: 'header-menu',
                items: headerMenuEntries,
              }}
              headerMenuDropdownOpen={headerMenuOpen}
              setHeaderMenuDropdownOpen={setHeaderMenuOpen}
              logo={logo}
              smallLogo={smallLogo}
              menu={
                <>
                  {authResult != null && (
                    <div className={'organization-container ' + (collapsed ? 'spt-collapsed-sidebar' : '')}>
                      <OrganizationBox />
                    </div>
                  )}
                  <Menu
                    mode="inline"
                    defaultSelectedKeys={[]}
                    className={collapsed ? 'spt-collapsed-sidebar' : ''}
                    selectedKeys={[selectedMenu]}
                    onClick={({ key }) => setSelectedMenu(key as string)}
                  >
                    {!unauthorized && (
                      <>
                        {userPermissionsSet.has(PermissionsConstants.ManagePartnerOrganization) && (
                          <Menu.Item key="organizations" icon={<BankOutlined className={collapsed ? 'collapsed-icon' : ''} />}>
                            <Link to={`organizations`}>{t('Organizations')}</Link>
                          </Menu.Item>
                        )}
                        {userPermissionsSet.has(PermissionsConstants.ManageUsers) &&
                          userPermissionsSet.has(PermissionsConstants.ManagePartnerOrganization) && (
                            <Menu.Item key="users" icon={<UserOutlined className={collapsed ? 'collapsed-icon' : ''} />}>
                              <Link to={`users`}>{t('Users')}</Link>
                            </Menu.Item>
                          )}
                        {userPermissionsSet.has(PermissionsConstants.ManageRoles) &&
                          userPermissionsSet.has(PermissionsConstants.ManagePartnerOrganization) && (
                            <Menu.Item key="roles" icon={<AuditOutlined className={collapsed ? 'collapsed-icon' : ''} />}>
                              <Link to={`roles`}>{t('Roles')}</Link>
                            </Menu.Item>
                          )}
                        {userPermissionsSet.has(PermissionsConstants.ManageRoles) &&
                          userPermissionsSet.has(PermissionsConstants.ManagePartnerOrganization) && (
                            <Menu.Item
                              key="partnerroles"
                              className={collapsed ? '' : 'menu-item-link'}
                              icon={<AuditOutlined className={collapsed ? 'collapsed-icon' : ''} />}
                            >
                              <Link to={`partnerroles`}>{t('PartnerRoles')}</Link>
                            </Menu.Item>
                          )}
                        {userPermissionsSet.has(PermissionsConstants.ManageGroups) &&
                          userPermissionsSet.has(PermissionsConstants.ManagePartnerOrganization) && (
                            <Menu.Item key="groups" icon={<TeamOutlined className={collapsed ? 'collapsed-icon' : ''} />}>
                              <Link to={`groups`}>{t('Groups')}</Link>
                            </Menu.Item>
                          )}
                        {((userPermissionsSet.has(PermissionsConstants.ManageLicenses) && licenseManagerRoleValid) ||
                          hasGlobalLicenseManagerPermission ||
                          userPermissionsSet.has(PermissionsConstants.ApproveLicenses)) && (
                          <Menu.Item key="licensing" icon={<KeyOutlined className={collapsed ? 'collapsed-icon' : ''} />}>
                            <Link to={`licenses`}>{t('Licenses')}</Link>
                          </Menu.Item>
                        )}
                        {userPermissionsSet.has(PermissionsConstants.ManagePartnerOrganization) && (
                          <Menu.Item
                            key="statistics"
                            className={collapsed ? '' : 'menu-item-link'}
                            icon={<FundOutlined className={collapsed ? 'collapsed-icon' : ''} />}
                          >
                            <Link to={`statistics`}>{t('Statistics')}</Link>
                          </Menu.Item>
                        )}
                      </>
                    )}
                  </Menu>
                </>
              }
              logout={
                <Menu mode="inline" defaultSelectedKeys={[]} selectedKeys={[selectedMenu]} onClick={({ key }) => setSelectedMenu(key as string)}>
                  <Menu.Item className={collapsed ? '' : 'eula-link'} icon={<ReadOutlined style={{ display: collapsed ? 'inherit' : 'none' }} />}>
                    <Link to={`eula`}>{t('EULATitle')}</Link>
                  </Menu.Item>

                  <div
                    style={{
                      color: '#89909a',
                      display: 'flex',
                      justifyContent: 'left',
                      alignItems: 'left',
                      marginBottom: '20px',
                      marginLeft: '5px',
                      fontSize: 'small',
                      marginTop: '15px',
                    }}
                  >
                    <span>v{config.appVersion}</span>
                  </div>
                </Menu>
              }
              collapsed={collapsed}
              onCollapse={() => setCollapsed(!collapsed)}
              i18nConfig={{
                languages: ['en', 'es', 'pt', 'fr', 'ru'],
                defaultLanguage: 'en',
              }}
            >
              {unauthorized ? (
                <Suspense fallback={<Spin spinning />}>
                  <Routes>
                    <Route element={<UnauthorizedPage />} />
                  </Routes>
                </Suspense>
              ) : (
                <NotificationsContext>
                  <Suspense fallback={<Spin spinning />}>
                    <Routes>
                      <Route path={`/*`} element={<HomePage />} />
                      <Route
                        path={`organizations/*`}
                        element={
                          userPermissionsSet.has(PermissionsConstants.ManagePartnerOrganization) ? <OrganizationsPage /> : <UnauthorizedPage />
                        }
                      />
                      <Route path={`notifications/*`} element={<NotificationsTableView />} />
                      <Route
                        path={`users/*`}
                        element={
                          userPermissionsSet.has(PermissionsConstants.ManageUsers) &&
                          userPermissionsSet.has(PermissionsConstants.ManagePartnerOrganization) ? (
                            <UsersPage />
                          ) : (
                            <UnauthorizedPage />
                          )
                        }
                      />
                      <Route
                        path={`roles/*`}
                        element={
                          userPermissionsSet.has(PermissionsConstants.ManageRoles) &&
                          userPermissionsSet.has(PermissionsConstants.ManagePartnerOrganization) ? (
                            <RolesPage />
                          ) : (
                            <UnauthorizedPage />
                          )
                        }
                      />
                      <Route
                        path={`partnerroles/*`}
                        element={
                          userPermissionsSet.has(PermissionsConstants.ManageRoles) &&
                          userPermissionsSet.has(PermissionsConstants.ManagePartnerOrganization) ? (
                            <PartnerRolesPage />
                          ) : (
                            <UnauthorizedPage />
                          )
                        }
                      />
                      <Route
                        path={`groups/*`}
                        element={
                          userPermissionsSet.has(PermissionsConstants.ManageGroups) &&
                          userPermissionsSet.has(PermissionsConstants.ManagePartnerOrganization) ? (
                            <GroupsPage />
                          ) : (
                            <UnauthorizedPage />
                          )
                        }
                      />
                      <Route
                        path={`licenses/*`}
                        element={
                          (userPermissionsSet.has(PermissionsConstants.ManageLicenses) && licenseManagerRoleValid) ||
                          hasGlobalLicenseManagerPermission ||
                          userPermissionsSet.has(PermissionsConstants.ApproveLicenses) ? (
                            <LicensesPage />
                          ) : (
                            <UnauthorizedPage />
                          )
                        }
                      />
                      <Route
                        path={`statistics`}
                        element={userPermissionsSet.has(PermissionsConstants.ManagePartnerOrganization) ? <StatisticsPage /> : <UnauthorizedPage />}
                      />
                      <Route path={`eula`} element={<EulaPage />} />
                      <Route path={`unauthorized`} element={<UnauthorizedPage />} />
                      <Route element={<NotFoundPage />} />
                    </Routes>
                  </Suspense>
                </NotificationsContext>
              )}
            </Shell>
          </OrganizationExportImportContext.Provider>
        </div>
      )}
    </SecureContent>
  );
};

export default AdminIndex;
